import { Capacitor } from "@capacitor/core";

/**
 * Returns true when the app is running on localhost (dev machine) or a local
 * emulator/test build. Used to tag newly created user accounts as dev-only
 * so they don't skew real user metrics in the admin dashboard.
 *
 * IMPORTANT: Capacitor native builds serve the webview from "https://localhost"
 * (see capacitor.config.ts), so window.location.hostname === "localhost" on
 * every installed Android/iOS app. Bail out on native so real Play Store / App
 * Store users aren't misclassified as dev accounts.
 */
export const isLocalhost = (): boolean => {
  if (typeof window === "undefined") return false;
  if (Capacitor.isNativePlatform()) return false;

  const host = window.location.hostname;

  // Defence in depth: any known production domain is NEVER dev, even if
  // somehow other checks would say otherwise. Real users were getting
  // misflagged with the Capacitor-only check, so this is the ground truth.
  if (
    host === "onyxlife.app" ||
    host.endsWith(".onyxlife.app") ||
    host.endsWith(".netlify.app")
  ) {
    return false;
  }

  return (
    host === "localhost" ||
    host === "127.0.0.1" ||
    host === "0.0.0.0" ||
    host.endsWith(".local") ||
    host.startsWith("192.168.") ||
    host.startsWith("10.0.")
  );
};

/**
 * Diagnostic info captured alongside devAccount: true. Lets us see in the
 * Firestore user doc exactly why isLocalhost() returned true for any given
 * signup — e.g. "host=192.168.1.10 native=false". Cheap insurance against
 * a future false-positive mystery.
 */
export const getDevAccountReason = (): string => {
  if (typeof window === "undefined") return "no-window";
  return `host=${window.location.hostname} native=${Capacitor.isNativePlatform()} platform=${Capacitor.getPlatform()}`;
};
